import type { AppSettings, WhatsAppMessage } from '@/lib/schemas/db'

import type { NewWhatsAppMessageInput } from '../domain/models'
import type {
  ClockPort,
  IdGeneratorPort,
  WhatsAppMessagesRepositoryPort,
} from '../ports'

/**
 * Queued messages wait this long before they may be sent, so staff can cancel.
 * Fallback when no app_settings record configures whatsapp_send_delay_seconds.
 */
export const WHATSAPP_SEND_GRACE_PERIOD_MS = 30_000

/** Grace period from app settings, falling back to the 30s default. */
export function whatsappSendDelayMs(settings?: AppSettings): number {
  const seconds = settings?.whatsappSendDelaySeconds
  return typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0
    ? seconds * 1000
    : WHATSAPP_SEND_GRACE_PERIOD_MS
}

export function queueWhatsAppMessage(
  input: NewWhatsAppMessageInput,
  deps: {
    clock: ClockPort
    idGenerator: IdGeneratorPort
    whatsappMessagesRepo: WhatsAppMessagesRepositoryPort
    /** Grace delay before the message may send; defaults to WHATSAPP_SEND_GRACE_PERIOD_MS. */
    graceMs?: number
    /**
     * Whether dispatch is switched on. When explicitly `false`, the message is
     * queued as 'Held' rather than 'Queued' — the server sender ignores Held, so
     * re-enabling sending won't auto-dispatch it; it waits for a manual resend.
     */
    sendingEnabled?: boolean
  },
): {
  message: WhatsAppMessage
  transaction: ReturnType<WhatsAppMessagesRepositoryPort['insert']>
} {
  const timestamp = deps.clock.now()
  const sendAfter = new Date(
    new Date(timestamp).getTime() + (deps.graceMs ?? WHATSAPP_SEND_GRACE_PERIOD_MS),
  ).toISOString()
  const message: WhatsAppMessage = {
    id: deps.idGenerator.generateId(),
    createdAt: timestamp,
    updatedAt: timestamp,
    status: deps.sendingEnabled === false ? 'Held' : 'Queued',
    sendAfter,
    ...input,
  }
  const transaction = deps.whatsappMessagesRepo.insert(message)
  return { message, transaction }
}

/**
 * After a message is successfully sent, resends to the same recipient are held
 * back for this long — a guard against accidentally firing the same template
 * (e.g. "your device is ready") twice. This is our own UX safeguard, not a
 * WhatsApp platform limit: approved templates may be sent at any time.
 */
export const WHATSAPP_RESEND_COOLDOWN_MS = 24 * 60 * 60 * 1000

/**
 * Recipient phone numbers that received a Sent message within `windowMs` before
 * `now`. Used to disable Resend so a fresh send isn't duplicated.
 */
export function recentlySentPhones(
  messages: WhatsAppMessage[],
  now: string,
  windowMs: number = WHATSAPP_RESEND_COOLDOWN_MS,
): Set<string> {
  const cutoff = new Date(now).getTime() - windowMs
  const phones = new Set<string>()
  for (const message of messages) {
    if (message.status !== 'Sent' || !message.sentAt) continue
    if (new Date(message.sentAt).getTime() >= cutoff) phones.add(message.toPhone)
  }
  return phones
}

export function graceRemainingMs(message: WhatsAppMessage, now: string): number {
  const remaining = new Date(message.sendAfter).getTime() - new Date(now).getTime()
  return Math.max(0, remaining)
}

export function isCancellable(message: WhatsAppMessage, now: string): boolean {
  return message.status === 'Queued' && graceRemainingMs(message, now) > 0
}

export function cancelWhatsAppMessage(
  id: string,
  deps: {
    clock: ClockPort
    whatsappMessagesRepo: WhatsAppMessagesRepositoryPort
  },
) {
  const timestamp = deps.clock.now()
  const message = deps.whatsappMessagesRepo.get(id)
  if (!message || !isCancellable(message, timestamp)) return null
  return deps.whatsappMessagesRepo.update(id, (draft) => {
    draft.status = 'Cancelled'
    draft.cancelledAt = timestamp
    draft.updatedAt = timestamp
  })
}

/**
 * Re-queue a message so the sender dispatches it again — used to retry a Failed
 * or Cancelled message, or to push one that was stuck while sending was off. A
 * fresh grace window applies (so it can be cancelled), and the previous
 * send/cancel/failure markers are cleared.
 */
export function resendWhatsAppMessage(
  id: string,
  deps: {
    clock: ClockPort
    whatsappMessagesRepo: WhatsAppMessagesRepositoryPort
    /** Grace delay before the resend may dispatch; defaults to the standard grace. */
    graceMs?: number
  },
) {
  const message = deps.whatsappMessagesRepo.get(id)
  if (!message) return null
  const timestamp = deps.clock.now()
  const sendAfter = new Date(
    new Date(timestamp).getTime() + (deps.graceMs ?? WHATSAPP_SEND_GRACE_PERIOD_MS),
  ).toISOString()
  return deps.whatsappMessagesRepo.update(id, (draft) => {
    draft.status = 'Queued'
    draft.sendAfter = sendAfter
    draft.sentAt = undefined
    draft.cancelledAt = undefined
    draft.failureReason = undefined
    draft.providerSid = undefined
    draft.updatedAt = timestamp
  })
}

/**
 * Park every currently-queued message as 'Held' — called when sending is
 * switched off. Held messages never auto-dispatch when sending is re-enabled;
 * only a manual resend re-queues them. Returns how many were parked. The server
 * enforces the same on the settings save; this keeps the open UI in step.
 */
export function holdQueuedWhatsAppMessages(
  messages: WhatsAppMessage[],
  deps: {
    clock: ClockPort
    whatsappMessagesRepo: WhatsAppMessagesRepositoryPort
  },
): number {
  const timestamp = deps.clock.now()
  let held = 0
  for (const message of messages) {
    if (message.status !== 'Queued') continue
    deps.whatsappMessagesRepo.update(message.id, (draft) => {
      draft.status = 'Held'
      draft.updatedAt = timestamp
    })
    held++
  }
  return held
}

export function markWhatsAppMessageSent(
  id: string,
  deps: {
    clock: ClockPort
    whatsappMessagesRepo: WhatsAppMessagesRepositoryPort
  },
) {
  const timestamp = deps.clock.now()
  const message = deps.whatsappMessagesRepo.get(id)
  if (message?.status !== 'Queued') return null
  return deps.whatsappMessagesRepo.update(id, (draft) => {
    draft.status = 'Sent'
    draft.sentAt = timestamp
    draft.updatedAt = timestamp
  })
}

export function pendingWhatsAppMessages(
  messages: WhatsAppMessage[],
): WhatsAppMessage[] {
  return messages
    .filter((message) => message.status === 'Queued')
    .sort((a, b) => a.sendAfter.localeCompare(b.sendAfter))
}

export function sentWhatsAppMessages(
  messages: WhatsAppMessage[],
): WhatsAppMessage[] {
  return messages
    .filter((message) => message.status === 'Sent')
    .sort((a, b) => (b.sentAt ?? b.updatedAt).localeCompare(a.sentAt ?? a.updatedAt))
}
